/**
 * Pure helpers behind the Data refinery tree, kept out of the components
 * so they are testable without a DOM.
 *
 * The api hands the page one benchmark's tree: the latest scoring night's
 * tests, each with the proven tasks generated from it. A qm-bench task is
 * generated from a stem that several seed tests share, so the same task
 * sits under every one of them; everything here that counts or selects
 * tasks goes by `taskKey`, so such a task is one task on the page.
 */

import { z } from 'zod'

import type { RefineryBenchmark, RefineryTask, RefineryTaskKey, RefineryTest } from '@/api/generated/model'
import { toggled } from '@/components/training/heldOutTests'

import { MAX_CARDS, REFINERY_COPY, type TaskCase, type TestVerdict, taskKey, testVerdict } from './rows'

/**
 * The benchmark the page opens on when the URL names none: the one the
 * scoring night scores, else the first the evals config names.
 */
export function defaultBenchmark(benchmarks: RefineryBenchmark[]): string | undefined {
  return (benchmarks.find((benchmark) => benchmark.scored) ?? benchmarks[0])?.id
}

/** The verdicts the Show menu offers, in its order. */
const VERDICT_FILTERS = [
  'model_limitation',
  'flagged',
  'needs_human',
  'inconclusive',
  'passing',
  'noRound',
] as const satisfies readonly TestVerdict[]

/**
 * What the Show menu keeps, one at a time: every test, the tests of one
 * verdict, the tests the base policy scored 0 on before any training step,
 * or the tasks the base policy passed on every run the gate made.
 */
const TREE_FILTERS = ['all', ...VERDICT_FILTERS, 'policyFails', 'alwaysPassed'] as const
export type TreeFilter = (typeof TREE_FILTERS)[number]

/** A menu value the Show menu offers. */
export function isTreeFilter(value: string): value is TreeFilter {
  return (TREE_FILTERS as readonly string[]).includes(value)
}

function isVerdictFilter(filter: TreeFilter): filter is TestVerdict {
  return (VERDICT_FILTERS as readonly string[]).includes(filter)
}

/** Whether the filter hides anything. */
export function isFiltered(filter: TreeFilter): boolean {
  return filter !== 'all'
}

/** The base policy's step-0 reward on the test is 0. Unscored is not a fail. */
export function policyFails(test: RefineryTest): boolean {
  return test.base_policy_validation !== null && test.base_policy_validation.reward === 0
}

/** Every base-policy run of the task passed. Unmeasured is not a pass. */
export function alwaysPassed(task: RefineryTask): boolean {
  const policy = task.base_policy
  return policy !== null && policy.runs > 0 && policy.passes === policy.runs
}

/** The tasks the filter keeps. A verdict is a test's, so a verdict keeps no task under no test. */
export function visibleTasks(tasks: RefineryTask[], filter: TreeFilter): RefineryTask[] {
  if (isVerdictFilter(filter)) return []
  return filter === 'alwaysPassed' ? tasks.filter(alwaysPassed) : tasks
}

/**
 * The tests the filter keeps, each with the tasks it keeps. Under the task
 * filter a test with no task left is out too: the row would say nothing
 * the filter asked for.
 */
export function visibleTests(tests: RefineryTest[], filter: TreeFilter): RefineryTest[] {
  if (filter === 'policyFails') return tests.filter(policyFails)
  if (isVerdictFilter(filter)) return tests.filter((test) => testVerdict(test) === filter)
  if (filter === 'all') return tests
  return tests
    .map((test) => ({ ...test, tasks: visibleTasks(test.tasks, filter) }))
    .filter((test) => test.tasks.length > 0)
}

/** What the Show menu counts beside its options. */
export interface FilterCounts {
  /** Tests of each verdict, in the menu's order. */
  verdicts: { verdict: TestVerdict; tests: number }[]
  policyFails: number
  /** Distinct tasks, the ones under no test among them. */
  alwaysPassed: number
}

export function filterCounts(tests: RefineryTest[], unresolved: RefineryTask[]): FilterCounts {
  return {
    verdicts: VERDICT_FILTERS.map((verdict) => ({
      verdict,
      tests: tests.filter((test) => testVerdict(test) === verdict).length,
    })),
    policyFails: tests.filter(policyFails).length,
    alwaysPassed: distinctTasks(tests, unresolved).filter(alwaysPassed).length,
  }
}

/** Every task of the tree once, in the order it is first listed. */
export function distinctTasks(tests: RefineryTest[], unresolved: RefineryTask[]): RefineryTask[] {
  const seen = new Set<string>()
  const tasks: RefineryTask[] = []
  for (const task of [...tests.flatMap((test) => test.tasks), ...unresolved]) {
    const key = taskKey(task)
    if (seen.has(key)) continue
    seen.add(key)
    tasks.push(task)
  }
  return tasks
}

/** What is ticked: tests by `test_id`, tasks by `taskKey`. */
export interface Ticked {
  tests: ReadonlySet<string>
  tasks: ReadonlySet<string>
}

export const NOTHING_TICKED: Ticked = { tests: new Set(), tasks: new Set() }

/** The selection with one task ticked or unticked, under every test that lists it. */
export function toggleTask(ticked: Ticked, task: TaskCase): Ticked {
  return { ...ticked, tasks: toggled(ticked.tasks, taskKey(task)) }
}

/**
 * The selection with a test and every task under it ticked, or with both
 * unticked when the test is ticked. A task shared with another ticked test
 * is unticked too: a tick names the task, not the row.
 */
export function toggleTest(ticked: Ticked, test: Pick<RefineryTest, 'test_id' | 'tasks'>): Ticked {
  const tests = new Set(ticked.tests)
  const tasks = new Set(ticked.tasks)
  const keys = test.tasks.map(taskKey)
  if (tests.delete(test.test_id)) {
    for (const key of keys) tasks.delete(key)
  } else {
    tests.add(test.test_id)
    for (const key of keys) tasks.add(key)
  }
  return { tests, tasks }
}

/** A test's path without the `#<n>` seed fragment qm-bench appends (refinery_tree.module_path). */
export function modulePath(module: string): string {
  return module.split('#', 1)[0] ?? module
}

/** One ticked test as Generate tasks names it: its case name, and its path as a request names it. */
export interface TickedTest {
  family: string
  module: string
}

/**
 * The ticked tests, once per path: the seeds of one qm-bench folder are
 * one test to generation, named by the first seed's case name.
 */
export function selectedTests(tests: RefineryTest[], ids: ReadonlySet<string>): TickedTest[] {
  const byModule = new Map<string, TickedTest>()
  for (const test of tests) {
    const module = modulePath(test.module)
    if (ids.has(test.test_id) && !byModule.has(module)) byModule.set(module, { family: test.family, module })
  }
  return [...byModule.values()]
}

/** The ticked tasks as an exclusions save names them, split by what a save would change. */
export interface TickedTasks {
  /** Ticked tasks the dataset builds may train on today. */
  excludable: RefineryTaskKey[]
  /** Ticked tasks staff keep out of the builds today. */
  includable: RefineryTaskKey[]
}

/** The ticked tasks of the tree, each once (a task sits under every seed test of its stem). */
export function selectedTasks(
  tests: RefineryTest[],
  unresolved: RefineryTask[],
  keys: ReadonlySet<string>,
): TickedTasks {
  const picked: TickedTasks = { excludable: [], includable: [] }
  const seen = new Set<string>()
  for (const task of distinctTasks(tests, unresolved)) {
    const id = `${task.batch}/${task.task_id}`
    if (!keys.has(taskKey(task)) || seen.has(id)) continue
    seen.add(id)
    const key: RefineryTaskKey = { batch: task.batch, task_id: task.task_id }
    if (task.excluded) picked.includable.push(key)
    else picked.excludable.push(key)
  }
  return picked
}

const CARDS = z
  .string()
  .regex(/^\d+$/)
  .transform(Number)
  .pipe(z.number().int().min(1).max(MAX_CARDS))

/** The cards field of Generate tasks: empty plans each test at its tier's count. */
export type ParsedCards = { kind: 'ok'; cards: number | null } | { kind: 'error'; error: string }

export function parseCards(text: string): ParsedCards {
  const trimmed = text.trim()
  if (trimmed === '') return { kind: 'ok', cards: null }
  const parsed = CARDS.safeParse(trimmed)
  return parsed.success
    ? { kind: 'ok', cards: parsed.data }
    : { kind: 'error', error: REFINERY_COPY.actions.cardsInvalid }
}
